import * as signalR from '@microsoft/signalr';
import ApiUrl from './config';

// Тот же ключ, что и в AuthContext.tsx — берём токен судьи, чтобы хаб мог
// проверить право на отправку событий (см. RequireMatchManagement на backend).
const getStoredToken = (): string => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.token || '';
  } catch {
    return '';
  }
};

export const createMatchHubConnection = (): signalR.HubConnection => {
  return new signalR.HubConnectionBuilder()
    .withUrl(`${ApiUrl}/hubs/match`, {
      // withCredentials: true потребовал бы CORS-политику с конкретными origin вместо AllowAnyOrigin.
      // Нам это не нужно — токен передаём сами через accessTokenFactory.
      withCredentials: false,
      accessTokenFactory: () => getStoredToken(),
    })
    .withAutomaticReconnect([0, 1000, 2000, 5000, 10000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();
};

export type OsaekomiSide = 'home' | 'away' | null;

export interface MatchLiveState {
  matchId: number;
  homeName: string;
  awayName: string;

  homeIppon: number;
  homeWazaAri: number;
  homeYuko: number;
  homeShido: number;

  awayIppon: number;
  awayWazaAri: number;
  awayYuko: number;
  awayShido: number;

  isStarted: boolean;
  isPaused: boolean;

  // Момент отправки снимка на машине судьи (ISO). Нужен только чтобы посчитать
  // удержание относительно того же снимка; часы табло с ним сравнивать нельзя.
  timerAnchorUtc: string | null;
  timeRemainingMsAtAnchor: number;
  isTimerRunning: boolean;
  isGoldenScore?: boolean;
  isHantei?: boolean;

  osaekomiSide: OsaekomiSide;
  osaekomiStartedUtc: string | null;
}

export interface MatchEndedPayload {
  matchId: number;
  winnerName: string;
}

export interface MatLockDto {
  matNumber: string;
  controllerId: string;
  matchId: number | null;
  isLive: boolean;
}

export interface MatClaimResult {
  accepted: boolean;
  matNumber: string;
  message?: string | null;
  occupied: boolean;
  isLive: boolean;
}

const finiteMs = (value: number | string | null | undefined): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

/**
 * Остаток времени поединка для табло.
 *
 * Нельзя считать как Date.now() минус timerAnchorUtc: часы монитора над ковром
 * и ноутбука судьи часто расходятся на минуты, и тогда вместо 3:00 бывает 0:00
 * или ~25:00. Отсчитываем локально от момента, когда снимок пришёл на это табло.
 */
export const remainingMsFromSnapshot = (
  state: MatchLiveState,
  receivedAtMs: number,
  nowMs: number
): number => {
  const remainingAtSend = finiteMs(state.timeRemainingMsAtAnchor);
  const delta = Math.max(0, nowMs - receivedAtMs);
  if (state.isGoldenScore) {
    return state.isTimerRunning ? remainingAtSend + delta : remainingAtSend;
  }
  if (!state.isTimerRunning) return remainingAtSend;
  return finiteMs(remainingAtSend - delta);
};

/** Секунды удержания: дельта двух меток судьи + локальное время после получения снимка. */
export const osaekomiSecondsFromSnapshot = (
  state: MatchLiveState,
  receivedAtMs: number,
  nowMs: number
): number => {
  if (!state.osaekomiSide || !state.osaekomiStartedUtc) return 0;
  const started = Date.parse(state.osaekomiStartedUtc);
  const sentAt = state.timerAnchorUtc ? Date.parse(state.timerAnchorUtc) : Number.NaN;
  const elapsedAtSend = Number.isFinite(started) && Number.isFinite(sentAt)
    ? Math.max(0, sentAt - started)
    : 0;
  return Math.floor(finiteMs(elapsedAtSend + (nowMs - receivedAtMs)) / 1000);
};
